import React, { FC, useCallback } from 'react';
import { Checkbox } from '@clearcover/atlas';
import { getIn, useFormikContext } from 'formik';
import { Row, Column, FormInput } from 'Shared/components';
import { Label } from 'Shared/components/FormInput/Variants';
import { GetConfigResponseType } from 'QuoteApp/services/endpoints/GetConfig';
import {
  FormValuesType,
  VehicleFieldsType,
} from 'QuoteApp/services/endpoints/GetDetails';
import { useEdit, useFieldData } from 'QuoteApp/utils/contexts';
import {
  getFieldErrors,
  formatAddressDisplay,
  generateFieldDataTestId,
} from 'QuoteApp/utils';
import { VehicleSelection } from '../VehicleSelection';
import { LienHolderForm } from './LienHolderForm';
import classNames from 'classnames';
import { useAppContext } from 'QuoteApp/services/providers';
import { GaragingAddress } from 'QuoteApp/components';
import { EMDASH } from 'Shared/utils';
import { antiTheftOptions, VehicleFormFieldKeys } from './options';
import styles from './styles.module.css';
import { VehicleUsage } from './VehicleUsageForm';
import { BusinessUseForm } from './BusinessUseForm';

type VehicleFormPropsType = {
  appId: string;
  config: GetConfigResponseType['vehicles'];
  vehicleId: string;
};

export const VehicleForm: FC<VehicleFormPropsType> = ({
  config,
  vehicleId,
}) => {
  const {
    setFieldValue,
    handleChange,
    handleBlur,
    touched,
    errors,
    values,
    initialValues,
  } = useFormikContext<FormValuesType>();
  const { details, config: appConfig } = useAppContext();

  const isAccessEnabled = !!appConfig?.accessEnabled;

  const needsGaragingAddress = Object.keys(details.vehicles).some(
    vehicleId => details.vehicles[vehicleId]?.needsGaragingAddress
  );
  const showGaragingAddressSection = needsGaragingAddress && !isAccessEnabled;
  const showGaragingAddressForm =
    values.vehicles[vehicleId].garagedAtPrimaryAddress === 'false';

  const primaryAddress = formatAddressDisplay(
    details.address[details.principalInsuredId]
  );

  const isGaragingAddressInvalid =
    details.vehicles[vehicleId].garagingAddressValidation?.isAddressValid ===
    false;

  const addressOutcomeValue =
    details.vehicles[vehicleId].garagingAddress.garagingAddressOutcome.value;

  const {
    state: { value: primaryAddressState },
  } = details.address[details.principalInsuredId];

  const { editingId } = useEdit();
  const { alwaysShowErrors, serverErrors, editableFields } = useFieldData();
  const getFullFieldName = useCallback(
    (fieldName: keyof Omit<VehicleFieldsType, 'editable' | 'lienHolder'>) =>
      `vehicles.${vehicleId}.${fieldName}`,
    [vehicleId]
  );

  const { required: mileageRequired } = appConfig?.vehicles?.mileage || {
    required: true,
  };
  const readOnly = editingId !== vehicleId;

  const getInputProps = useCallback(
    (fieldName: VehicleFormFieldKeys, required = true) => {
      const fullFieldName = getFullFieldName(fieldName);
      return {
        name: fullFieldName,
        onChange: handleChange,
        onBlur: handleBlur,
        value: values.vehicles[vehicleId][fieldName],
        disabled: readOnly || !getIn(editableFields, fullFieldName),
        errorMessage: getFieldErrors<FormValuesType>(
          fullFieldName,
          required,
          alwaysShowErrors,
          errors,
          serverErrors,
          values,
          touched,
          initialValues
        ),
      };
    },
    [
      editableFields,
      initialValues,
      serverErrors,
      alwaysShowErrors,
      readOnly,
      errors,
      getFullFieldName,
      handleBlur,
      handleChange,
      touched,
      values,
      vehicleId,
    ]
  );

  return (
    <>
      <VehicleSelection vehicleId={vehicleId} />
      <Row>
        {!isAccessEnabled && (
          <Column variant="third">
            <FormInput
              data-testid="usage"
              {...getInputProps('usage')}
              variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
              label="How is this vehicle primarily used?"
              onChange={(value: string) =>
                setFieldValue(getFullFieldName('usage'), value)
              }
              options={config.usage}
            />
          </Column>
        )}
        {mileageRequired && (
          <Column variant="third">
            <FormInput
              data-testid="annual-mileage"
              {...getInputProps('annualMileage')}
              variant={readOnly ? 'readOnly' : 'text'}
              label="Annual Mileage"
            />
          </Column>
        )}
        <Column />
      </Row>

      {isAccessEnabled ? (
        <Row>
          <Column variant="third">
            <FormInput
              {...getInputProps('hasAntiTheftDevice')}
              variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
              data-testid="has-anti-theft-device"
              label="Vehicle equipped with an anti-theft device"
              onChange={(value: string) =>
                setFieldValue(getFullFieldName('hasAntiTheftDevice'), value)
              }
              options={{ true: 'Yes', false: 'No' }}
            />
          </Column>
          <Column />
        </Row>
      ) : (
        <div role="group" aria-labelledby="anti-theft">
          <Label
            id="anti-theft"
            className={classNames({
              [styles.readOnlyLabel]: readOnly,
            })}
          >
            Anti-theft Features
          </Label>
          <div className={styles.checkboxContainer}>
            {/* Handle the case when every option is not checked */
            readOnly &&
              antiTheftOptions.every(
                option => values.vehicles?.[vehicleId][option.key] === 'false'
              ) && <span className={styles.readOnlyValue}>{EMDASH}</span>}

            {antiTheftOptions.map(option => {
              const checked =
                values.vehicles?.[vehicleId][option.key] === 'true';
              if (readOnly) {
                return checked ? (
                  <span className={styles.readOnlyValue} key={option.key}>
                    {option.label}
                  </span>
                ) : null;
              }
              return (
                <Checkbox
                  key={option.key}
                  data-testid={`anti-theft-${generateFieldDataTestId(
                    option.key
                  )}`}
                  className={styles.checkbox}
                  {...getInputProps(option.key)}
                  value={checked}
                  label={option.label}
                  onChange={value => {
                    setFieldValue(
                      getFullFieldName(option.key),
                      value.toString()
                    );
                  }}
                />
              );
            })}
          </div>
        </div>
      )}
      <LienHolderForm config={config} vehicleId={vehicleId} />
      {isAccessEnabled && (
        <BusinessUseForm config={config} vehicleId={vehicleId} />
      )}
      {!isAccessEnabled && config.rideshareUse.required && (
        <Row>
          <Column variant="third">
            <FormInput
              {...getInputProps('rideshareUse')}
              variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
              data-testid="rideshare-use"
              label="Rideshare?"
              onChange={(value: string) =>
                setFieldValue(getFullFieldName('rideshareUse'), value)
              }
              options={{ true: 'Yes', false: 'No' }}
            />
          </Column>
          <Column />
        </Row>
      )}
      {showGaragingAddressSection && (
        <Row>
          <Column variant="third">
            <FormInput
              {...getInputProps('garagedAtPrimaryAddress')}
              variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
              data-testid="garaged-at-primary-address"
              label={`Is this vehicle parked at ${primaryAddress}?`}
              tooltip="Select ‘Yes’ if the vehicle is parked on the street near the address listed."
              tooltipPosition="right"
              onChange={(value: string) => {
                setFieldValue(
                  getFullFieldName('garagedAtPrimaryAddress'),
                  value
                );
              }}
              options={{ true: 'Yes', false: 'No' }}
            />
          </Column>
          {showGaragingAddressForm &&
            config.parkingDetails.parkingLocationReason.required && (
              <Column variant="third">
                <FormInput
                  name={`vehicles.${vehicleId}.parkingDetails.parkingLocationReason`}
                  onBlur={handleBlur}
                  value={
                    values.vehicles[vehicleId].parkingDetails
                      .parkingLocationReason
                  }
                  errorMessage={getFieldErrors<FormValuesType>(
                    `vehicles.${vehicleId}.parkingDetails.parkingLocationReason`,
                    true,
                    alwaysShowErrors,
                    errors,
                    serverErrors,
                    values,
                    touched,
                    initialValues
                  )}
                  label="For what reason is this vehicle parked at a separate location?"
                  variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                  disabled={
                    editingId !== vehicleId ||
                    !getIn(
                      editableFields,
                      `vehicles.${vehicleId}.parkingDetails.parkingLocationReason`
                    )
                  }
                  data-testid="vehicle-in-state-parking-duration"
                  onChange={(value: string) => {
                    setFieldValue(
                      `vehicles.${vehicleId}.parkingDetails.parkingLocationReason`,
                      value
                    );
                  }}
                  options={config.parkingDetails.parkingLocationReason.options}
                />
              </Column>
            )}
        </Row>
      )}
      {showGaragingAddressSection && showGaragingAddressForm && (
        <Row>
          <Column variant="full">
            <GaragingAddress
              vehicleId={vehicleId}
              hasError={
                addressOutcomeValue !== null && isGaragingAddressInvalid
              }
              primaryAddressState={primaryAddressState}
              disabled={
                readOnly ||
                !getIn(
                  editableFields,
                  `vehicles.${vehicleId}.garagingAddress.addressLine1`
                )
              }
              readOnly={readOnly}
            />
          </Column>
        </Row>
      )}
      <Row>
        {config.parkingDetails.inStateParkingDuration.required && (
          <Column variant="third">
            <FormInput
              name={`vehicles.${vehicleId}.parkingDetails.inStateParkingDuration`}
              onBlur={handleBlur}
              value={
                values.vehicles[vehicleId].parkingDetails.inStateParkingDuration
              }
              errorMessage={getFieldErrors<FormValuesType>(
                `vehicles.${vehicleId}.parkingDetails.inStateParkingDuration`,
                true,
                alwaysShowErrors,
                errors,
                serverErrors,
                values,
                touched,
                initialValues
              )}
              label={`In a typical year, is this vehicle parked in ${primaryAddressState} for at least 10 months?`}
              tooltip={
                'If any driver is an active military member currently deployed (domestic or overseas) or on temporary duty assignment, select ‘Yes’.'
              }
              variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
              disabled={
                editingId !== vehicleId ||
                !getIn(
                  editableFields,
                  `vehicles.${vehicleId}.parkingDetails.inStateParkingDuration`
                )
              }
              data-testid="vehicle-in-state-parking-duration"
              onChange={(value: string) => {
                setFieldValue(
                  `vehicles.${vehicleId}.parkingDetails.inStateParkingDuration`,
                  value
                );
              }}
              options={{ atLeast10Months: 'Yes', lessThan10Months: 'No' }}
            />
          </Column>
        )}
        {config.parkingType.required && (
          <Column variant="third">
            <FormInput
              name={`vehicles.${vehicleId}.parkingType`}
              onBlur={handleBlur}
              value={values.vehicles[vehicleId].parkingType}
              errorMessage={getFieldErrors<FormValuesType>(
                `vehicles.${vehicleId}.parkingType`,
                true,
                alwaysShowErrors,
                errors,
                serverErrors,
                values,
                touched,
                initialValues
              )}
              label="When not in use, where is this vehicle typically parked?"
              variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
              disabled={
                editingId !== vehicleId ||
                !getIn(editableFields, `vehicles.${vehicleId}.parkingType`)
              }
              data-testid="vehicle-parking-type"
              onChange={(value: string) => {
                setFieldValue(`vehicles.${vehicleId}.parkingType`, value);
              }}
              options={config.parkingType.options}
            />
          </Column>
        )}
      </Row>
      {config.usageDetails.required && (
        <Row>
          <VehicleUsage
            vehicleId={vehicleId}
            getInputProps={getInputProps}
            getFullFieldName={getFullFieldName}
            readOnly={readOnly}
          />
        </Row>
      )}
      {config.parkingType.required && (
        <Row>
          <Column variant="full">
            <div className={styles.disclosure}>
              Clearcover may request confirmation that the garaging residence
              provides the parking options stated by the customer. Failure to
              provide accurate information regarding the storage of a vehicle
              while not in use may result in the denial of a claim.
            </div>
          </Column>
        </Row>
      )}
    </>
  );
};
